// src/components/GuitarManagement.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaPlus, FaEdit, FaTrash, FaImage, FaSpinner,
  FaTimes, FaStar, FaBox, FaSearch, FaFilter,
  FaArrowUp, FaArrowDown, FaEye, FaEyeSlash,
  FaSave, FaUndo, FaTags, FaMusic, FaCheck,
  FaSortAmountDown, FaSortAmountUp, FaCamera,
  FaExclamationTriangle, FaInfoCircle
} from "react-icons/fa";
import { db, storage } from "../firebase";
import { 
  collection, query, orderBy, onSnapshot, 
  addDoc, updateDoc, deleteDoc, doc,
  serverTimestamp, where, getDocs
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const GuitarManagement = () => {
  const [guitars, setGuitars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGuitar, setEditingGuitar] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filterStock, setFilterStock] = useState("all");
  const [filterFeatured, setFilterFeatured] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    category: "",
    price: "",
    originalPrice: "",
    discountPrice: "",
    description: "",
    shortDescription: "",
    stock: "1",
    featured: false,
    active: true,
    imageUrls: [],
    specs: {
      body: "",
      neck: "",
      fretboard: "",
      bridge: "",
      pickupsNeck: "",
      pickupsBridge: "",
      tuners: "",
      strings: "",
      finish: "",
      scaleLength: "",
      frets: "",
      controls: "",
      weight: "",
      caseIncluded: false,
      year: "",
      country: ""
    },
    tags: [],
    seo: {
      title: "",
      description: "",
      keywords: ""
    }
  });

  const [newTag, setNewTag] = useState("");
  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedMainImage, setSelectedMainImage] = useState(0);

  // Gitarları yükle
  useEffect(() => {
    const q = query(collection(db, "guitars"));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const guitarsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Kategorileri ve markaları çıkar
      const uniqueCategories = [...new Set(guitarsData.map(g => g.category).filter(Boolean))];
      const uniqueBrands = [...new Set(guitarsData.map(g => g.brand).filter(Boolean))];
      
      setCategories(uniqueCategories);
      setBrands(uniqueBrands);
      setGuitars(guitarsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Resim yükleme fonksiyonu
  const uploadImage = async (file) => {
    try {
      // Dosya boyutu kontrolü (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("Dosya boyutu 5MB'dan küçük olmalıdır");
      }

      // Dosya tipi kontrolü
      if (!file.type.startsWith('image/')) {
        throw new Error("Sadece resim dosyaları yüklenebilir");
      }

      const storageRef = ref(storage, `guitars/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error("Resim yükleme hatası:", error);
      throw error;
    }
  };

const deleteImageFromStorage = async (imageUrl) => {
  try {
    // URL'den storage referansını çıkar
    // URL şu şekilde olabilir: https://firebasestorage.googleapis.com/...
    // Storage path'ini URL'den çıkarıyoruz
    const url = new URL(imageUrl);
    const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
    const imageRef = ref(storage, path);
    
    await deleteObject(imageRef);
    console.log("Resim storage'dan silindi:", path);
    return true;
  } catch (error) {
    console.error("Resim silme hatası:", error);
    
    // Eğer path bulunamadıysa, diğer formatı dene
    try {
      // Alternatif path formatı
      const pathMatch = imageUrl.match(/guitars\/.+$/);
      if (pathMatch) {
        const imageRef = ref(storage, pathMatch[0]);
        await deleteObject(imageRef);
        return true;
      }
    } catch (secondError) {
      console.error("Alternatif silme hatası:", secondError);
    }
    
    return false;
  }
};

  // Filtrelenmiş ve sıralanmış gitarlar
  const filteredGuitars = guitars
    .filter(guitar => {
      const matchesSearch = 
        guitar.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guitar.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guitar.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guitar.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = filterCategory === "all" || guitar.category === filterCategory;
      const matchesStock = 
        filterStock === "all" ? true :
        filterStock === "inStock" ? (guitar.stock > 0) :
        filterStock === "outOfStock" ? (guitar.stock === 0) : true;
      
      const matchesFeatured = 
        filterFeatured === "all" ? true :
        filterFeatured === "featured" ? guitar.featured :
        filterFeatured === "notFeatured" ? !guitar.featured : true;
      
      return matchesSearch && matchesCategory && matchesStock && matchesFeatured;
    })
    .sort((a, b) => {
      let aValue = a[sortBy] || "";
      let bValue = b[sortBy] || "";
      
      // Tarih sıralaması için
      if (sortBy === "createdAt" || sortBy === "updatedAt") {
        aValue = aValue?.seconds || 0;
        bValue = bValue?.seconds || 0;
      }
      
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  // Form validasyonu
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Gitar adı gereklidir";
    if (!formData.brand.trim()) newErrors.brand = "Marka gereklidir";
    if (!formData.category.trim()) newErrors.category = "Kategori gereklidir";
    if (!formData.price || parseFloat(formData.price) <= 0) newErrors.price = "Geçerli bir fiyat girin";
    if (!formData.stock || parseInt(formData.stock) < 0) newErrors.stock = "Geçerli bir stok miktarı girin";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    alert("Lütfen gerekli alanları doldurun");
    return;
  }

  setUploading(true);

  try {
    // Yeni resimleri yükle
    const uploadedUrls = [];
    for (const file of selectedImages) {
      try {
        const url = await uploadImage(file);
        uploadedUrls.push(url);
      } catch (error) {
        console.error("Resim yükleme hatası:", error);
        alert(`${file.name} yüklenirken hata oluştu: ${error.message}`);
      }
    }

    // Tüm resim URL'lerini birleştir (önce yeni resimler, sonra mevcutlar)
    let allImageUrls = [...uploadedUrls];
    
    if (editingGuitar) {
      // Mevcut resimleri sırayla ekle (ana resim ilk sırada)
      allImageUrls = [...allImageUrls, ...formData.imageUrls];
    }

    // Gitar verilerini hazırla
    const guitarData = {
      name: formData.name.trim(),
      brand: formData.brand.trim(),
      category: formData.category.trim(),
      price: parseFloat(formData.price),
      originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
      discountPrice: formData.discountPrice ? parseFloat(formData.discountPrice) : null,
      description: formData.description.trim(),
      shortDescription: formData.shortDescription.trim(),
      stock: parseInt(formData.stock),
      featured: formData.featured,
      active: formData.active,
      imageUrls: allImageUrls, // Sıralı resim listesi
      specs: formData.specs,
      tags: formData.tags.filter(tag => tag.trim() !== ""),
      seo: {
        title: formData.seo.title.trim() || formData.name.trim(),
        description: formData.seo.description.trim() || formData.shortDescription.trim(),
        keywords: formData.seo.keywords.trim()
      },
      updatedAt: serverTimestamp()
    };

    if (editingGuitar) {
      await updateDoc(doc(db, "guitars", editingGuitar.id), guitarData);
    } else {
      guitarData.createdAt = serverTimestamp();
      await addDoc(collection(db, "guitars"), guitarData);
    }

    setShowModal(false);
    resetForm();
  } catch (error) {
    console.error("Gitar kaydetme hatası:", error);
    alert("Gitar kaydedilirken bir hata oluştu: " + error.message);
  } finally {
    setUploading(false);
  }
};

  // Formu sıfırla
  const resetForm = () => {
    setFormData({
      name: "",
      brand: "",
      category: "",
      price: "",
      originalPrice: "",
      discountPrice: "",
      description: "",
      shortDescription: "",
      stock: "1",
      featured: false,
      active: true,
      imageUrls: [],
      specs: {
        body: "",
        neck: "",
        fretboard: "",
        bridge: "",
        pickupsNeck: "",
        pickupsBridge: "",
        tuners: "",
        strings: "",
        finish: "",
        scaleLength: "",
        frets: "",
        controls: "",
        weight: "",
        caseIncluded: false,
        year: "",
        country: ""
      },
      tags: [],
      seo: {
        title: "",
        description: "",
        keywords: ""
      }
    });
    setSelectedImages([]);
    setImagePreviews([]);
    setEditingGuitar(null);
    setErrors({});
    setNewTag("");
    setSelectedMainImage(0);
  };

  // Gitar düzenle
  const handleEdit = (guitar) => {
    setEditingGuitar(guitar);
    setFormData({
      name: guitar.name || "",
      brand: guitar.brand || "",
      category: guitar.category || "",
      price: guitar.price?.toString() || "",
      originalPrice: guitar.originalPrice?.toString() || "",
      discountPrice: guitar.discountPrice?.toString() || "",
      description: guitar.description || "",
      shortDescription: guitar.shortDescription || "",
      stock: guitar.stock?.toString() || "1",
      featured: guitar.featured || false,
      active: guitar.active !== undefined ? guitar.active : true,
      imageUrls: guitar.imageUrls || [],
      specs: guitar.specs || {
        body: "",
        neck: "",
        fretboard: "",
        bridge: "",
        pickupsNeck: "",
        pickupsBridge: "",
        tuners: "",
        strings: "",
        finish: "",
        scaleLength: "",
        frets: "",
        controls: "",
        weight: "",
        caseIncluded: false,
        year: "",
        country: ""
      },
      tags: guitar.tags || [],
      seo: guitar.seo || {
        title: "",
        description: "",
        keywords: ""
      }
    });
    setShowModal(true);
  };

  // Gitar sil
  const handleDelete = async (id) => {
    if (!window.confirm("Bu gitarı ve tüm resimlerini silmek istediğinize emin misiniz? Bu işlem geri alınamaz!")) {
      return;
    }

    try {
      const guitar = guitars.find(g => g.id === id);
      
      // Resimleri storage'dan sil
      if (guitar.imageUrls && guitar.imageUrls.length > 0) {
        const deletePromises = guitar.imageUrls.map(url => deleteImageFromStorage(url));
        await Promise.allSettled(deletePromises);
      }
      
      // Firestore'dan sil
      await deleteDoc(doc(db, "guitars", id));
    } catch (error) {
      console.error("Gitar silme hatası:", error);
      alert("Gitar silinirken bir hata oluştu");
    }
  };

  // Resim seçimi
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Maksimum 10 resim kontrolü
    if (selectedImages.length + files.length > 10) {
      alert("Maksimum 10 resim yükleyebilirsiniz");
      return;
    }
    
    setSelectedImages(prev => [...prev, ...files]);
    
    // Preview oluştur
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(prev => [...prev, ...newPreviews]);
  };

  // Resim sırasını değiştir - DÜZELTİLMİŞ VERSİYON
const moveImage = (index, direction) => {
  const allImages = [...formData.imageUrls];
  const newIndex = direction === 'up' ? index - 1 : index + 1;
  
  // Geçerli indeks kontrolü
  if (newIndex < 0 || newIndex >= allImages.length) {
    return;
  }
  
  // Resimleri yer değiştir
  [allImages[index], allImages[newIndex]] = [allImages[newIndex], allImages[index]];
  
  setFormData(prev => ({
    ...prev,
    imageUrls: allImages
  }));
  
  // Ana resim index'ini güncelleyin
  if (selectedMainImage === index) {
    setSelectedMainImage(newIndex);
  } else if (selectedMainImage === newIndex) {
    setSelectedMainImage(index);
  }
};

 // Resim sil - DÜZELTİLMİŞ VERSİYON
const removeImage = async (index, isPreview = false) => {
  if (isPreview) {
    // Preview resmini sil
    const updatedPreviews = [...imagePreviews];
    const updatedFiles = [...selectedImages];
    
    // URL'i temizle (memory leak önlemek için)
    URL.revokeObjectURL(updatedPreviews[index]);
    
    updatedPreviews.splice(index, 1);
    updatedFiles.splice(index, 1);
    setImagePreviews(updatedPreviews);
    setSelectedImages(updatedFiles);
  } else {
    // Database'den resmi sil
    const imageUrl = formData.imageUrls[index];
    
    if (window.confirm("Bu resmi silmek istediğinize emin misiniz?")) {
      try {
        // Önce listeyi güncelle
        const newImageUrls = [...formData.imageUrls];
        newImageUrls.splice(index, 1);
        
        setFormData(prev => ({
          ...prev,
          imageUrls: newImageUrls
        }));
        
        // Ana resim index'ini güncelle
        if (selectedMainImage === index) {
          setSelectedMainImage(0);
        } else if (selectedMainImage > index) {
          setSelectedMainImage(selectedMainImage - 1);
        }
        
        // Storage'dan resmi sil
        await deleteImageFromStorage(imageUrl);
        
      } catch (error) {
        console.error("Resim silme hatası:", error);
        alert("Resim silinirken bir hata oluştu");
      }
    }
  }
};

// Main image seç - DÜZELTİLMİŞ VERSİYON
const setAsMainImage = (index) => {
  const allImages = [...formData.imageUrls];
  
  if (index >= 0 && index < allImages.length) {
    // Seçilen resmi başa al
    const selectedImage = allImages[index];
    const remainingImages = allImages.filter((_, i) => i !== index);
    
    const newImageUrls = [selectedImage, ...remainingImages];
    
    setFormData(prev => ({
      ...prev,
      imageUrls: newImageUrls
    }));
    setSelectedMainImage(0); // Artık ilk resim ana resim
  }
};

  // Tag ekle
  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag("");
    }
  };

  // Tag sil
  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Sıralamayı değiştir
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Loading durumu
  if (loading) {
    return (
      <div className="text-center py-12">
        <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
        <div className="text-gray-400 text-lg">Gitarlar yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header ve Kontroller */}
      <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Gitarlar Yönetimi</h1>
            <p className="text-gray-400">
              Toplam {guitars.length} gitar • {filteredGuitars.length} görüntüleniyor
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Arama */}
            <div className="relative flex-1 sm:w-64">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                placeholder="Gitar ara..."
              />
            </div>

            {/* Filtreler Butonu */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 text-gray-300 hover:text-white rounded-xl hover:border-blue-500/30 transition-all"
            >
              <FaFilter />
              Filtreler
            </button>

            {/* Yeni Gitar */}
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-blue-500/20"
            >
              <FaPlus />
              Yeni Gitar
            </button>
          </div>
        </div>

        {/* Filtreler Paneli */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-gray-700/50 overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Kategori Filtresi */}
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Kategori</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="all">Tüm Kategoriler</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Stok Filtresi */}
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Stok Durumu</label>
                  <select
                    value={filterStock}
                    onChange={(e) => setFilterStock(e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="all">Tümü</option>
                    <option value="inStock">Stokta Var</option>
                    <option value="outOfStock">Stokta Yok</option>
                  </select>
                </div>

                {/* Öne Çıkan Filtresi */}
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Öne Çıkan</label>
                  <select
                    value={filterFeatured}
                    onChange={(e) => setFilterFeatured(e.target.value)}
                    className="w-full px-4 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                  >
                    <option value="all">Tümü</option>
                    <option value="featured">Öne Çıkanlar</option>
                    <option value="notFeatured">Öne Çıkmayanlar</option>
                  </select>
                </div>

                {/* Sıralama */}
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Sırala</label>
                  <div className="flex gap-2">
                    <select
                      value={sortBy}
                      onChange={(e) => handleSort(e.target.value)}
                      className="flex-1 px-4 py-2.5 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                    >
                      <option value="createdAt">Eklenme Tarihi</option>
                      <option value="name">İsim</option>
                      <option value="price">Fiyat</option>
                      <option value="stock">Stok</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                      className="px-4 py-2.5 bg-gray-800/50 border border-gray-700/50 rounded-xl text-gray-300 hover:text-white hover:border-blue-500/30 transition-all"
                    >
                      {sortOrder === "asc" ? <FaSortAmountUp /> : <FaSortAmountDown />}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Gitarlar Grid */}
      {filteredGuitars.length === 0 ? (
        <div className="text-center py-12 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50">
          <FaMusic size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Gitar bulunamadı</h3>
          <p className="text-gray-400 mb-6">Arama kriterlerinize uygun gitar yok</p>
          <button
            onClick={() => {
              setSearchTerm("");
              setFilterCategory("all");
              setFilterStock("all");
              setFilterFeatured("all");
              resetForm();
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all"
          >
            <FaPlus />
            İlk Gitarı Ekleyin
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredGuitars.map((guitar) => (
            <motion.div
              key={guitar.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden hover:border-blue-500/30 transition-all duration-300"
            >
              {/* Resim */}
              <div className="relative h-48 overflow-hidden bg-gradient-to-br from-gray-900 to-black">
                {guitar.imageUrls?.[0] ? (
                  <img
                    src={guitar.imageUrls[0]}
                    alt={guitar.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FaMusic className="text-gray-700 text-4xl" />
                  </div>
                )}
                
                {/* Badgeler */}
                <div className="absolute top-3 left-3 flex flex-col gap-1">
                  {guitar.featured && (
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded-full border border-amber-500/30">
                      <FaStar size={10} className="inline mr-1" />
                      Öne Çıkan
                    </span>
                  )}
                  {guitar.stock === 0 && (
                    <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full border border-red-500/30">
                      Stokta Yok
                    </span>
                  )}
                  {guitar.discountPrice && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                      İndirimli
                    </span>
                  )}
                </div>
              </div>
              
              {/* İçerik */}
              <div className="p-5">
                {/* Başlık ve Kategoriler */}
                <div className="mb-4">
                  <h3 className="font-bold text-white text-lg mb-2 line-clamp-1">
                    {guitar.name}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                      {guitar.brand}
                    </span>
                    <span className="px-2 py-1 bg-gray-700/50 text-gray-300 text-xs rounded-full">
                      {guitar.category}
                    </span>
                    {guitar.tags?.slice(0, 2).map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Kısa Açıklama */}
                {guitar.shortDescription && (
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {guitar.shortDescription}
                  </p>
                )}

                {/* Fiyat ve Stok */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700/50">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-white">
                        {guitar.price?.toLocaleString('tr-TR')} ₺
                      </span>
                      {guitar.discountPrice && (
                        <span className="text-lg font-bold text-green-400">
                          {guitar.discountPrice?.toLocaleString('tr-TR')} ₺
                        </span>
                      )}
                    </div>
                    {guitar.originalPrice && guitar.discountPrice && (
                      <div className="text-gray-400 text-sm line-through">
                        {guitar.originalPrice?.toLocaleString('tr-TR')} ₺
                      </div>
                    )}
                    <div className={`text-sm ${guitar.stock > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      Stok: {guitar.stock}
                    </div>
                  </div>

                  {/* Aksiyon Butonları */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(guitar)}
                      className="p-2 bg-blue-500/10 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded-lg transition-colors"
                      title="Düzenle"
                    >
                      <FaEdit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(guitar.id)}
                      className="p-2 bg-red-500/10 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <FaTrash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

     {/* Gitar Ekleme/Düzenleme Modalı */}
<AnimatePresence>
  {showModal && (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700/50 w-full max-w-6xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {editingGuitar ? "Gitarı Düzenle" : "Yeni Gitar Ekle"}
              </h2>
              <p className="text-gray-400 text-sm">
                Tüm alanları doldurarak gitarı kaydedin
              </p>
            </div>
            <button
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
            >
              <FaTimes size={20} className="text-gray-400" />
            </button>
          </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Sekmeler */}
                  <div className="border-b border-gray-700/50">
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(false)}
                        className={`pb-3 px-1 border-b-2 ${!showAdvanced ? 'border-blue-500 text-white' : 'border-transparent text-gray-400'} transition-all`}
                      >
                        Temel Bilgiler
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(true)}
                        className={`pb-3 px-1 border-b-2 ${showAdvanced ? 'border-blue-500 text-white' : 'border-transparent text-gray-400'} transition-all`}
                      >
                        Detaylar & SEO
                      </button>
                    </div>
                  </div>

                  {!showAdvanced ? (
                    /* TEMEL BİLGİLER */
                    <div className="space-y-6">
                      {/* Resim Yönetimi */}
                      <div className="bg-gray-900/30 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h3 className="text-lg font-bold text-white">Resim Yönetimi</h3>
                            <p className="text-gray-400 text-sm">
                              İlk resim ana resim olarak kullanılır. Maksimum 10 resim.
                            </p>
                          </div>
                          <div className="relative">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={handleImageSelect}
                              className="hidden"
                              id="imageUpload"
                              disabled={uploading || (formData.imageUrls.length + selectedImages.length >= 10)}
                            />
                            <label
                              htmlFor="imageUpload"
                              className={`flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer transition-all ${
                                uploading || (formData.imageUrls.length + selectedImages.length >= 10)
                                  ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                              }`}
                            >
                              <FaCamera />
                              Resim Ekle
                            </label>
                          </div>
                        </div>

{/* Resim Grid - DÜZELTİLMİŞ VERSİYON */}
<div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
  {/* Mevcut Resimler */}
  {formData.imageUrls.map((img, index) => (
    <div key={`existing-${index}`} className="relative group">
      <div className={`aspect-square rounded-lg overflow-hidden border-2 ${
        index === selectedMainImage ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-gray-700'
      }`}>
        <img
          src={img}
          alt={`Gitar ${index + 1}`}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/300x300?text=Resim+Yüklenemedi';
          }}
        />
      </div>
      
      {/* Resim Kontrolleri */}
      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center gap-2">
        {/* Ana Resim Butonu */}
        <button
          type="button"
          onClick={() => setAsMainImage(index)}
          className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
            index === selectedMainImage 
              ? 'bg-blue-600 text-white cursor-default' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
          disabled={index === selectedMainImage}
        >
          {index === selectedMainImage ? '✓ Ana Resim' : 'Ana Resim Yap'}
        </button>
        
        {/* Sıralama Butonları */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => moveImage(index, 'up')}
            disabled={index === 0}
            className="p-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Yukarı taşı"
          >
            <FaArrowUp size={12} />
          </button>
          <button
            type="button"
            onClick={() => moveImage(index, 'down')}
            disabled={index === formData.imageUrls.length - 1}
            className="p-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Aşağı taşı"
          >
            <FaArrowDown size={12} />
          </button>
        </div>
        
        {/* Silme Butonu */}
        <button
          type="button"
          onClick={() => removeImage(index, false)}
          className="px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors"
          title="Resmi sil"
        >
          Sil
        </button>
      </div>
      
      {/* Sıra Numarası */}
      <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
        {index + 1}
      </div>
      
      {/* Ana Resim İşareti */}
      {index === selectedMainImage && (
        <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
          <FaStar size={8} />
          Ana
        </div>
      )}
    </div>
  ))}
  
  {/* Yeni Eklenen Resimler (Preview) */}
  {imagePreviews.map((img, index) => (
    <div key={`preview-${index}`} className="relative">
      <div className="aspect-square rounded-lg overflow-hidden border-2 border-dashed border-gray-600">
        <img
          src={img}
          alt={`Preview ${index + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      <button
        type="button"
        onClick={() => removeImage(index, true)}
        className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
        title="Resmi kaldır"
      >
        <FaTimes size={10} />
      </button>
      <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
        Yeni
      </div>
    </div>
  ))}
  
  {/* Resim Ekleme Butonu */}
  <div className="relative">
    <input
      type="file"
      multiple
      accept="image/*"
      onChange={handleImageSelect}
      className="hidden"
      id="imageUpload"
      disabled={uploading || (formData.imageUrls.length + imagePreviews.length >= 10)}
    />
    <label
      htmlFor="imageUpload"
      className={`aspect-square rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${
        uploading || (formData.imageUrls.length + imagePreviews.length >= 10)
          ? 'border-gray-700 bg-gray-900/20 text-gray-600 cursor-not-allowed'
          : 'border-gray-600 hover:border-blue-500 hover:bg-blue-500/10 text-gray-400 hover:text-blue-400'
      }`}
    >
      <FaCamera size={24} />
      <span className="text-xs text-center px-2">
        {formData.imageUrls.length + imagePreviews.length}/10
      </span>
      <span className="text-xs">Resim Ekle</span>
    </label>
  </div>
  
  {/* Boş Durum */}
  {formData.imageUrls.length + imagePreviews.length === 0 && (
    <div className="col-span-full text-center py-12">
      <FaImage className="text-gray-600 text-4xl mx-auto mb-4" />
      <p className="text-gray-400 mb-2">Henüz resim eklenmedi</p>
      <p className="text-gray-500 text-sm">Maksimum 10 resim yükleyebilirsiniz</p>
    </div>
  )}
</div>
                      </div>

                      {/* Temel Bilgi Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Gitar Adı */}
                        <div>
                          <label className="block text-gray-300 mb-2">
                            Gitar Adı *
                            {errors.name && (
                              <span className="text-red-400 text-sm ml-2">
                                <FaExclamationTriangle className="inline mr-1" />
                                {errors.name}
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:ring-2 ${
                              errors.name 
                                ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' 
                                : 'border-gray-700/50 focus:border-blue-500/50 focus:ring-blue-500/20'
                            }`}
                            placeholder="Örn: Fender Stratocaster"
                          />
                        </div>

                        {/* Marka */}
                        <div>
                          <label className="block text-gray-300 mb-2">
                            Marka *
                            {errors.brand && (
                              <span className="text-red-400 text-sm ml-2">
                                <FaExclamationTriangle className="inline mr-1" />
                                {errors.brand}
                              </span>
                            )}
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={formData.brand}
                              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                              list="brands-list"
                              className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:ring-2 ${
                                errors.brand 
                                  ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' 
                                  : 'border-gray-700/50 focus:border-blue-500/50 focus:ring-blue-500/20'
                              }`}
                              placeholder="Örn: Fender"
                            />
                            <datalist id="brands-list">
                              {brands.map(brand => (
                                <option key={brand} value={brand} />
                              ))}
                            </datalist>
                          </div>
                        </div>

                        {/* Kategori */}
                        <div>
                          <label className="block text-gray-300 mb-2">
                            Kategori *
                            {errors.category && (
                              <span className="text-red-400 text-sm ml-2">
                                <FaExclamationTriangle className="inline mr-1" />
                                {errors.category}
                              </span>
                            )}
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={formData.category}
                              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                              list="categories-list"
                              className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:ring-2 ${
                                errors.category 
                                  ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' 
                                  : 'border-gray-700/50 focus:border-blue-500/50 focus:ring-blue-500/20'
                              }`}
                              placeholder="Örn: Elektro Gitar"
                            />
                            <datalist id="categories-list">
                              {categories.map(cat => (
                                <option key={cat} value={cat} />
                              ))}
                            </datalist>
                          </div>
                        </div>

                        {/* Fiyatlar */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-gray-300 mb-2">
                              Fiyat (₺) *
                              {errors.price && (
                                <span className="text-red-400 text-sm ml-2">
                                  <FaExclamationTriangle className="inline mr-1" />
                                  {errors.price}
                                </span>
                              )}
                            </label>
                            <input
                              type="number"
                              value={formData.price}
                              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                              className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:ring-2 ${
                                errors.price 
                                  ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' 
                                  : 'border-gray-700/50 focus:border-blue-500/50 focus:ring-blue-500/20'
                              }`}
                              placeholder="25000"
                              step="0.01"
                              min="0"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-gray-300 mb-2">Orijinal Fiyat (₺)</label>
                              <input
                                type="number"
                                value={formData.originalPrice}
                                onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                                placeholder="İndirimsiz fiyat"
                                step="0.01"
                                min="0"
                              />
                            </div>

                            <div>
                              <label className="block text-gray-300 mb-2">İndirimli Fiyat (₺)</label>
                              <input
                                type="number"
                                value={formData.discountPrice}
                                onChange={(e) => setFormData({ ...formData, discountPrice: e.target.value })}
                                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                                placeholder="İndirimli fiyat"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Stok ve Durum */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-gray-300 mb-2">
                              Stok Miktarı *
                              {errors.stock && (
                                <span className="text-red-400 text-sm ml-2">
                                  <FaExclamationTriangle className="inline mr-1" />
                                  {errors.stock}
                                </span>
                              )}
                            </label>
                            <input
                              type="number"
                              value={formData.stock}
                              onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                              className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:ring-2 ${
                                errors.stock 
                                  ? 'border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20' 
                                  : 'border-gray-700/50 focus:border-blue-500/50 focus:ring-blue-500/20'
                              }`}
                              placeholder="10"
                              min="0"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-3 p-3 bg-gray-900/30 rounded-lg">
                              <input
                                type="checkbox"
                                id="featured"
                                checked={formData.featured}
                                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                                className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
                              />
                              <label htmlFor="featured" className="text-gray-300">
                                Öne Çıkan
                              </label>
                            </div>

                            <div className="flex items-center gap-3 p-3 bg-gray-900/30 rounded-lg">
                              <input
                                type="checkbox"
                                id="active"
                                checked={formData.active}
                                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                                className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
                              />
                              <label htmlFor="active" className="text-gray-300">
                                Aktif
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Tag'ler */}
                        <div className="md:col-span-2 lg:col-span-3">
                          <label className="block text-gray-300 mb-2">Etiketler</label>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {formData.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-full text-sm"
                              >
                                {tag}
                                <button
                                  type="button"
                                  onClick={() => removeTag(tag)}
                                  className="hover:text-red-400"
                                >
                                  <FaTimes size={12} />
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newTag}
                              onChange={(e) => setNewTag(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                              className="flex-1 px-4 py-2 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                              placeholder="Etiket ekle ve Enter'a bas"
                            />
                            <button
                              type="button"
                              onClick={addTag}
                              className="px-4 py-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
                            >
                              <FaTags />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Açıklamalar */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-gray-300 mb-2">Kısa Açıklama</label>
                          <textarea
                            value={formData.shortDescription}
                            onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                            rows="2"
                            className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                            placeholder="Kısa ve öz açıklama (max 150 karakter)"
                            maxLength="150"
                          />
                          <div className="text-right text-gray-500 text-sm mt-1">
                            {formData.shortDescription.length}/150
                          </div>
                        </div>

                        <div>
                          <label className="block text-gray-300 mb-2">Detaylı Açıklama</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows="4"
                            className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                            placeholder="Detaylı gitar açıklaması..."
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* DETAYLAR & SEO */
                    <div className="space-y-8">
                      {/* Gitar Özellikleri */}
                      <div className="bg-gray-900/30 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-white mb-4">Gitar Özellikleri</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {[
                            { id: 'body', label: 'Body', placeholder: 'Örn: Alder' },
                            { id: 'neck', label: 'Neck', placeholder: 'Örn: Maple' },
                            { id: 'fretboard', label: 'Fretboard', placeholder: 'Örn: Roasted Maple' },
                            { id: 'scaleLength', label: 'Scale Length', placeholder: 'Örn: 25.5"' },
                            { id: 'frets', label: 'Frets', placeholder: 'Örn: 22' },
                            { id: 'bridge', label: 'Bridge', placeholder: 'Örn: Tonerider Hot Classic' },
                            { id: 'pickupsNeck', label: 'Neck Pickup', placeholder: 'Örn: G&B Humbucker' },
                            { id: 'pickupsBridge', label: 'Bridge Pickup', placeholder: 'Örn: Vintage Bridge' },
                            { id: 'tuners', label: 'Tuners', placeholder: 'Örn: Wilkinson Tuners' },
                            { id: 'strings', label: 'Strings', placeholder: 'Örn: Elixir Nanoweb 09-24' },
                            { id: 'finish', label: 'Finish', placeholder: 'Örn: Nitro Relic' },
                            { id: 'controls', label: 'Controls', placeholder: 'Örn: 1 Volume, 2 Tone' },
                            { id: 'weight', label: 'Weight', placeholder: 'Örn: 3.5 kg' },
                            { id: 'year', label: 'Year', placeholder: 'Örn: 2023' },
                            { id: 'country', label: 'Country', placeholder: 'Örn: USA' },
                          ].map((spec) => (
                            <div key={spec.id}>
                              <label className="block text-gray-300 mb-2">{spec.label}</label>
                              <input
                                type="text"
                                value={formData.specs[spec.id] || ''}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  specs: { ...prev.specs, [spec.id]: e.target.value }
                                }))}
                                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                                placeholder={spec.placeholder}
                              />
                            </div>
                          ))}
                          
                          <div className="flex items-center gap-3 p-4 bg-gray-900/50 rounded-xl">
                            <input
                              type="checkbox"
                              id="caseIncluded"
                              checked={formData.specs.caseIncluded || false}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                specs: { ...prev.specs, caseIncluded: e.target.checked }
                              }))}
                              className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500"
                            />
                            <label htmlFor="caseIncluded" className="text-gray-300">
                              Kılıf Dahil
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* SEO Ayarları */}
                      <div className="bg-gray-900/30 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-white mb-4">SEO Ayarları</h3>
                        <div className="space-y-6">
                          <div>
                            <label className="block text-gray-300 mb-2">SEO Başlığı</label>
                            <input
                              type="text"
                              value={formData.seo.title}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                seo: { ...prev.seo, title: e.target.value }
                              }))}
                              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                              placeholder="SEO için optimize edilmiş başlık"
                            />
                            <div className="text-gray-500 text-sm mt-2 flex items-center gap-2">
                              <FaInfoCircle />
                              {formData.seo.title ? (
                                <span>Google'da görünecek: "{formData.seo.title}"</span>
                              ) : (
                                <span>Varsayılan olarak gitar adı kullanılacak</span>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-300 mb-2">SEO Açıklaması</label>
                            <textarea
                              value={formData.seo.description}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                seo: { ...prev.seo, description: e.target.value }
                              }))}
                              rows="3"
                              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                              placeholder="SEO için optimize edilmiş açıklama (150-160 karakter)"
                              maxLength="160"
                            />
                            <div className="text-gray-500 text-sm mt-2 flex items-center gap-2">
                              <FaInfoCircle />
                              {formData.seo.description ? (
                                <span>Karakter: {formData.seo.description.length}/160</span>
                              ) : (
                                <span>Varsayılan olarak kısa açıklama kullanılacak</span>
                              )}
                            </div>
                          </div>

                          <div>
                            <label className="block text-gray-300 mb-2">Anahtar Kelimeler</label>
                            <input
                              type="text"
                              value={formData.seo.keywords}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                seo: { ...prev.seo, keywords: e.target.value }
                              }))}
                              className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700/50 rounded-xl text-white focus:outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20"
                              placeholder="gitar, elektro gitar, fender, stratocaster (virgülle ayırın)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Butonlar */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-700/50">
                    <div className="text-gray-400 text-sm">
                      {editingGuitar ? (
                        <div className="flex items-center gap-2">
                          <FaInfoCircle />
                          <span>Son güncelleme: {
                            editingGuitar.updatedAt 
                              ? new Date(editingGuitar.updatedAt.seconds * 1000).toLocaleString('tr-TR')
                              : 'Bilinmiyor'
                          }</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <FaInfoCircle />
                          <span>Tüm gerekli alanları doldurduğunuzdan emin olun</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setShowModal(false);
                          resetForm();
                        }}
                        className="flex items-center gap-2 px-6 py-3 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 rounded-xl transition-colors"
                      >
                        <FaUndo />
                        İptal
                      </button>
                      
                      <button
                        type="submit"
                        disabled={uploading}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-blue-500/20"
                      >
                        {uploading ? (
                          <>
                            <FaSpinner className="animate-spin" />
                            Kaydediliyor...
                          </>
                        ) : editingGuitar ? (
                          <>
                            <FaSave />
                            Güncelle
                          </>
                        ) : (
                          <>
                            <FaPlus />
                            Gitar Ekle
                          </>
                        )}
                      </button>
                    </div>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
</div>
  );
};

export default GuitarManagement;